import { doc, onSnapshot, query, serverTimestamp, setDoc, where } from "firebase/firestore";
import React, { useContext, useEffect, useState } from "react";
import { templatesRef } from "../firebase";
import { AuthContext } from "./AuthProvider";
import { setTemplatesSnapshot } from "../helperFunctions/templateStore";
import { getTemplateMessage } from "../helperFunctions/templateEngine";

// Templates are shared app-wide (not per-user) - same sharing model as Rates/Customers,
// scoped to the account's associatedAccounts group rather than a single uid, since this
// is business content everyone on the account should see and edit the same copy of.
export const TemplatesContext = React.createContext({ templates: {}, saveTemplateMessage: async () => {} });

export const TemplatesProvider = ({ children }) => {
  const { userData } = useContext(AuthContext);
  const [templates, setTemplates] = useState({});

  useEffect(() => {
    if (!userData?.uid || !userData?.associatedAccounts?.length) {
      return;
    }

    let q = query(templatesRef, where("createdBy", "in", userData.associatedAccounts));

    const unsubFromTemplates = onSnapshot(
      q,
      (snapshot) => {
        let map = {};
        snapshot.forEach((docSnap) => {
          map[docSnap.id] = docSnap.data();
        });
        setTemplates(map);
        setTemplatesSnapshot(map);
      },
      (err) => {
        // Falls back to the built-in default copy (see getTemplateMessage) - editing is
        // unavailable until Firestore rules allow read/write on the "templates" collection,
        // but preview/draft creation keeps working either way.
        console.warn("Templates snapshot failed, using built-in defaults:", err.message);
      }
    );

    return () => {
      unsubFromTemplates();
    };
  }, [userData?.uid, userData?.associatedAccounts]);

  const saveTemplateMessage = async (templateKey, message) => {
    let writes = [
      setDoc(
        doc(templatesRef, templateKey),
        { message, createdBy: userData.uid, updatedAt: serverTimestamp() },
        { merge: true }
      ),
    ];

    // If the vat_on/vat_off sibling of this key is still silently riding the pre-split
    // legacy (unsuffixed) key's content - see getTemplateMessage's legacyKey fallback -
    // fork it into its own explicit override at the same moment, with whatever it was
    // already effectively showing. Otherwise saving just this card would leave the sibling
    // permanently coupled to that shared legacy fallback with no way to notice it happened.
    let siblingKey = templateKey.replace(/__vat_(on|off)$/, (_, mode) =>
      mode === "on" ? "__vat_off" : "__vat_on"
    );

    if (siblingKey !== templateKey && templates[siblingKey]?.message === undefined) {
      writes.push(
        setDoc(
          doc(templatesRef, siblingKey),
          {
            message: getTemplateMessage(siblingKey, templates),
            createdBy: userData.uid,
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        )
      );
    }

    return Promise.all(writes);
  };

  return (
    <TemplatesContext.Provider value={{ templates, saveTemplateMessage }}>
      {children}
    </TemplatesContext.Provider>
  );
};
